// React
import React, {
  Children,
  cloneElement,
  useState,
  useMemo,
  useCallback,
} from "react";
import PropTypes from "prop-types";
// Helpers
import { isEmpty, map, get } from "@mefisto/utils";
// Framework
import { classnames } from "ui/classnames";
import {
  makeStyles,
  alpha,
  Box,
  Grid,
  Popover,
  MenuList,
  IconButton,
} from "ui/components";
import { PiDotsThreeOutlineDuotone as MoreIcon } from "@mefisto/icon";

////////////////////////////////////////////////////
/// Styles
////////////////////////////////////////////////////

const useStyles = makeStyles((theme) => ({
  transparent: {
    zIndex: 1,
    color: theme.palette.common.white,
    backdropFilter: "blur(5px)",
    backgroundColor: alpha(theme.palette.common.black, 0.5),
    "&:hover": {
      backgroundColor: alpha(theme.palette.common.black, 0.6),
    },
  },
}));

////////////////////////////////////////////////////
/// Component
////////////////////////////////////////////////////

const TileOptions = ({
  display = "permanent",
  color = "action.active",
  options,
}) => {
  // Styles
  const classes = useStyles();
  // State
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  // Handlers
  const handleOpen = useCallback((event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setOpen(true);
  }, []);
  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);
  // Memo
  const menuOptions = useMemo(() => {
    return Children.map(options, (child) => {
      return get(child, "props.display") === "menu"
        ? cloneElement(child, {
            onClose: handleClose,
          })
        : null;
    });
  }, [options, handleClose]);
  const standaloneOptions = useMemo(() => {
    return Children.map(options, (child) => {
      return get(child, "props.display") === "standalone"
        ? cloneElement(child, {
            onClose: handleClose,
          })
        : null;
    });
  }, [options, handleClose]);
  // Render
  return (
    <>
      {options && (
        <Box color={color}>
          <Grid container spacing={1} wrap="nowrap" alignItems="center">
            {map(standaloneOptions, (item, key) => (
              <Grid key={key} item>
                {item}
              </Grid>
            ))}
            {!isEmpty(menuOptions) && (
              <Grid item>
                <IconButton
                  value="more"
                  color="inherit"
                  className={classnames({
                    [classes.transparent]: display === "transparent",
                  })}
                  onClick={handleOpen}
                >
                  <MoreIcon />
                </IconButton>
                <Popover
                  keepMounted
                  open={open}
                  anchorEl={anchorEl}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  onClose={handleClose}
                >
                  <MenuList dense>{menuOptions}</MenuList>
                </Popover>
              </Grid>
            )}
          </Grid>
        </Box>
      )}
    </>
  );
};

TileOptions.propTypes = {
  display: PropTypes.oneOf(["permanent", "transparent"]),
  options: PropTypes.arrayOf(PropTypes.node),
};

export default TileOptions;
